import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Clock, AlertTriangle } from "lucide-react";
import { getPaymentStatus, getBooking } from "@/lib/api";

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const bookingId = params.get("booking_id");
  const [state, setState] = useState("checking");
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    let tries = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const s = await getPaymentStatus(sessionId);
        if (s.payment_status === "paid") { setState("paid"); setBooking(await getBooking(bookingId)); return; }
        if (["failed", "expired"].includes(s.payment_status)) { setState("failed"); return; }
      } catch { /* retry */ }
      if (++tries < 8) setTimeout(poll, 2000); else setState("timeout");
    };
    if (sessionId) poll(); else setState("failed");
    return () => { stop = true; };
  }, [sessionId, bookingId]);

  return (
    <div className="bg-[#F5F5F0] min-h-[70vh] flex items-center">
      <div className="max-w-xl mx-auto px-6 py-16 text-center" data-testid="booking-success">
        {state === "checking" && <><Loader2 className="w-10 h-10 animate-spin mx-auto text-[#001A4D]" /><p className="mt-4 text-black/60">Confirming your payment…</p></>}
        {state === "paid" && booking && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-[#001A4D]" />
            <p className="overline text-[#001A4D] mt-6">Payment received</p>
            <h1 className="font-serif text-4xl mt-2">Booking {booking.ref} is in</h1>
            <p className="mt-4 text-black/65 leading-relaxed">Thanks {booking.customer_name.split(" ")[0]}! Whangamatā Cycles will confirm your booking shortly. Your confirmation email{booking.tours.length ? " with your tour code(s)" : ""} will land at <strong>{booking.customer_email}</strong>.</p>
            <div className="mt-6 inline-flex items-center gap-2 text-sm text-black/55 bg-white rounded-full px-4 py-2 border border-black/10"><Clock className="w-4 h-4" /> {booking.hire_date}{booking.start_time ? ` · ${booking.start_time}` : ""} · {booking.hours}h · ${booking.total.toFixed(2)}</div>
            <div className="mt-8"><Link to="/" className="inline-flex h-12 px-6 items-center rounded-full bg-[#001A4D] text-white font-medium">Back to Echo</Link></div>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <AlertTriangle className="w-12 h-12 mx-auto text-[#001A4D]" />
            <h1 className="font-serif text-3xl mt-6">{state === "timeout" ? "Still confirming…" : "Payment not completed"}</h1>
            <p className="mt-3 text-black/65">{state === "timeout" ? "Your payment is taking a moment. If you were charged, your booking is safe — we'll email you." : "No charge was made. You can try again from the booking page."}</p>
            <div className="mt-8"><Link to="/book" className="inline-flex h-12 px-6 items-center rounded-full bg-[#0A0A0A] text-white font-medium">Back to booking</Link></div>
          </>
        )}
      </div>
    </div>
  );
}
